import { yupResolver } from "@hookform/resolvers/yup";
import type { CheckboxOption } from "@wayflyer/flyui";
import { CheckboxField, FormLayout, TextField, TwoColumnContainer, useMediaQuery } from "@wayflyer/flyui";
import { RhfDatePickerField, RhfDropdownField } from "@wayflyer/flyui-hook-form";
import dayjs, { type Dayjs } from "dayjs";
import React from "react";
import { type DefaultValues, useForm } from "react-hook-form";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import type * as Yup from "yup";

import { CurrencyInput } from "../currency-input/currency-input";

import { MESSAGES } from "./message-definitions";
import { makeSelectFundingSchema } from "./select-funding-entry-form.schema";

const EARLIEST_DAYS_OFFSET = 2;
const MAX_DAYS_OFFSET = 45;

const isValidPreferredFundsDeploymentDate = (givenDate: Dayjs): boolean => {
  const today = dayjs().startOf("day");
  const earliestValidDate = today.add(EARLIEST_DAYS_OFFSET, "day");
  const latestValidDate = today.add(MAX_DAYS_OFFSET, "day");

  // user can't pick a date outside of the date range
  if (givenDate.isBefore(earliestValidDate) || givenDate.isAfter(latestValidDate)) {
    return false;
  }

  // or weekends
  if (givenDate.day() === 0 || givenDate.day() === 6) {
    return false;
  }

  // or Monday if today is Friday
  if (givenDate.day() === 1 && today.day() === 5) {
    return false;
  }

  return true;
};

const getNextValidPreferredFundsDepoloymentDate = (): Dayjs | null => {
  const today = dayjs().startOf("day");
  for (let i = EARLIEST_DAYS_OFFSET; i < 7; i++) {
    const candidateDate = today.add(i, "day");
    if (isValidPreferredFundsDeploymentDate(candidateDate)) {
      return candidateDate;
    }
  }

  // if for some reason we don't find a valid date in the next 7 days just return null
  return null;
};

type SelectFundingSchema = ReturnType<typeof makeSelectFundingSchema>;
export type SelectFundingFormFields = Yup.InferType<SelectFundingSchema>;
type SelectFundingFormInput = Yup.InferType<SelectFundingSchema>;

const intendedUseOptions = (intl: IntlShape) => {
  return [
    {
      name: "marketing",
      label: intl.formatMessage(MESSAGES.intendedUseMarketing),
    },
    {
      name: "inventory",
      label: intl.formatMessage(MESSAGES.intendedUseInventory),
    },
    {
      name: "improve-cashflow",
      label: intl.formatMessage(MESSAGES.intendedUseImproveCashflow),
    },
    {
      name: "research-development",
      label: intl.formatMessage(MESSAGES.intendedUseResearchDevelopment),
    },
    {
      name: "business-expansion",
      label: intl.formatMessage(MESSAGES.intendedUseBusinessExpansion),
    },
    {
      name: "other",
      label: intl.formatMessage(MESSAGES.intendedUseOther),
    },
  ];
};

type SelectFundingEntryFormProps = {
  onSubmit: (formData: SelectFundingFormFields) => Promise<void>;
  initialValues: DefaultValues<SelectFundingFormInput>;
  formId: string;
};

export const SelectFundingEntryForm = ({ onSubmit, initialValues, formId }: SelectFundingEntryFormProps) => {
  const intl = useIntl();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;

  const schema = makeSelectFundingSchema(intl);

  const form = useForm<SelectFundingFormInput, unknown, SelectFundingFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
      preferredFundsDeploymentDate:
        initialValues.preferredFundsDeploymentDate || getNextValidPreferredFundsDepoloymentDate()?.format("YYYY-MM-DD"),
    },
  });

  const {
    formState: { errors },
  } = form;

  const onCheck = (isChecked: boolean, name: string) => {
    if (isChecked) {
      const previousFormEntry = form.getValues("intendedUse") ?? [];
      form.setValue("intendedUse", [...previousFormEntry, name]);
    } else {
      form.setValue(
        "intendedUse",
        form.getValues("intendedUse").filter((value) => value !== name)
      );
      if (name === "other") {
        form.setValue("intendedUseOther", "");
      }
    }
  };

  const formIntendedUseOptions = intendedUseOptions(intl).map((option) => {
    if (option.name === "other") {
      return {
        name: option.name,
        label: option.label,
        defaultChecked: initialValues?.intendedUse?.includes(option.name),
        // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
        checked: form.watch("intendedUse")?.includes(option.name),
        onChange: (checked) => {
          onCheck(checked, option.name);
        },
      } satisfies CheckboxOption;
    }
    return {
      name: option.name,
      label: option.label,
      defaultChecked: initialValues?.intendedUse?.includes(option.name),
      onChange: (checked) => {
        onCheck(checked, option.name);
      },
    } satisfies CheckboxOption;
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const isIntendedUseOtherChecked = form.watch("intendedUse")?.includes("other");

  return (
    <form id={formId} data-testid="select-funding-form" onSubmit={form.handleSubmit(onSubmit)}>
      <FormLayout>
        <CurrencyInput
          name="requested-funding-amount"
          label={intl.formatMessage(MESSAGES.howMuchFundingLabel)}
          helperText={intl.formatMessage(MESSAGES.howMuchFundingHelperText)}
          defaultCurrency={initialValues?.requestedAmountCurrency || "USD"}
          defaultValue={initialValues?.requestedAmount}
          onChange={({ unitId, unitValue }) => {
            form.setValue("requestedAmountCurrency", unitId);
            form.setValue("requestedAmount", unitValue);
          }}
          error={errors.requestedAmount?.message}
          analyticAttributes={{ "data-dd-privacy": "allow" }}
        />
        <CheckboxField
          name="select-funding-use"
          FieldsContainer={isMobile ? React.Fragment : TwoColumnContainer}
          label={intl.formatMessage(MESSAGES.reasonForFundingLabel)}
          helperText={intl.formatMessage(MESSAGES.reasonForFundingHelperText)}
          options={formIntendedUseOptions}
          error={errors.intendedUse?.message}
        />
        {isIntendedUseOtherChecked ? (
          <TextField
            name="other-input"
            label={intl.formatMessage(MESSAGES.intendedUseOtherSpecify)}
            // HACK: See https://github.com/wayflyer/wayflyer/pull/21846
            defaultValue={initialValues?.intendedUseOther as string | undefined}
            error={errors.intendedUseOther?.message}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
              form.setValue("intendedUseOther", target.value);
              form.trigger("intendedUseOther");
            }}
          />
        ) : null}
        <RhfDropdownField
          form={form}
          label={intl.formatMessage(MESSAGES.fundsDeploymentPreferenceLabel)}
          name="preferredFundsDeploymentChoice"
          options={[
            { id: "asap", text: intl.formatMessage(MESSAGES.preferredFundsDeploymentChoiceAsap) },
            { id: "specific-date", text: intl.formatMessage(MESSAGES.preferredFundsDeploymentChoiceSpecificDate) },
          ]}
        />
        {form.getValues("preferredFundsDeploymentChoice") === "specific-date" && (
          <RhfDatePickerField
            label={intl.formatMessage(MESSAGES.preferredFundsDeploymentDateLabel)}
            form={form}
            name="preferredFundsDeploymentDate"
            shouldDisableDate={(date: string) => !isValidPreferredFundsDeploymentDate(dayjs(date).startOf("day"))}
          />
        )}
      </FormLayout>
    </form>
  );
};
